import { FC, useEffect, useState } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { ContentContainerType } from 'types/enums';
import { ChakraPageTitle } from 'app/components/Core';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getPrivacySettings } from 'app/store/Profile/selectors';
import { updatePrivacySettingsEffect } from 'app/store/Profile/effects';
import { AllPrivacySettings } from 'types/profile';
import { SettingsBackButton } from 'app/components/Settings/SettingsBackButton';
import {
  PrivacyGroup,
  PrivacyGroupItem,
  PrivacyGroupProps,
} from './PrivacyGroup';
import { getPrivacyGroups } from './utils';

export const PrivacySettings: FC = () => {
  const dispatch = useAppDispatch();
  const privacySettings = useAppSelector(getPrivacySettings);
  const [privacyGroups, setPrivacyGroups] = useState(
    getPrivacyGroups(privacySettings)
  );

  useEffect(() => {
    setPrivacyGroups(getPrivacyGroups(privacySettings));
  }, [privacySettings]);

  const updatePrivacySettings = (privacySettings: AllPrivacySettings) => {
    dispatch(updatePrivacySettingsEffect(privacySettings));
  };

  const toggleMasterSwitch = (
    group: Omit<PrivacyGroupProps, 'handleToggle'>
  ) => {
    const basic = !group.master.enabled;
    updatePrivacySettings({
      privacySettingsCollab: {
        basic,
      },
      privacySettingsInternal: {
        basic,
      },
      ...(!basic ? { privacySettingsPublic: { basic } } : {}),
    });
  };

  const toggleGroupSwitch = (
    group: Omit<PrivacyGroupProps, 'handleToggle'>
  ) => {
    updatePrivacySettings({
      [group.master.id]: {
        basic: !group.master.enabled,
      },
    });
  };

  const toggleGroupItemSwitch = (
    group: Omit<PrivacyGroupProps, 'handleToggle'>,
    itemToToggle: PrivacyGroupItem
  ) => {
    updatePrivacySettings({
      [group.master.id]: {
        [itemToToggle.id]: !itemToToggle.enabled,
      },
    });
  };

  const handleToggle = (master: PrivacyGroupItem, item?: PrivacyGroupItem) => {
    const group = privacyGroups.find((g) => g.master.id === master.id);
    if (group) {
      const itemToToggle = group.items?.find((i) => i.id === item?.id);
      if (itemToToggle) {
        toggleGroupItemSwitch(group, itemToToggle);
      } else if (group.master.id === 'master') {
        toggleMasterSwitch(group);
      } else {
        toggleGroupSwitch(group);
      }
    }
  };

  return (
    <ContentContainer
      type={ContentContainerType.FORM}
      display="block"
      px={2}
      pt={4}
      pb="60px"
    >
      <SettingsBackButton />
      <Box py={4}>
        <ChakraPageTitle flex={1}>Privacy</ChakraPageTitle>
        <VStack gap={10}>
          {privacyGroups
            .filter(
              (_, index) => index === 0 || privacyGroups[0].master.enabled
            )
            .map((privacyGroup) => (
              <PrivacyGroup
                key={privacyGroup.master.id}
                {...privacyGroup}
                handleToggle={handleToggle}
              />
            ))}
        </VStack>
      </Box>
    </ContentContainer>
  );
};
